import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Container,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import { CheckCircle, Email, Unsubscribe as UnsubscribeIcon } from '@mui/icons-material';
import axios from 'axios';

interface UnsubscribeProps {
  email?: string;
  accountId?: string;
}

const Unsubscribe: React.FC<UnsubscribeProps> = ({ email: propEmail, accountId: propAccountId }) => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [accountId, setAccountId] = useState<string>(''); // eslint-disable-line @typescript-eslint/no-unused-vars

  useEffect(() => {
    // Get email and accountId from URL params if not provided as props
    const urlParams = new URLSearchParams(window.location.search);
    const emailFromUrl = urlParams.get('email');
    const accountIdFromUrl = urlParams.get('accountId');

    if (emailFromUrl) {
      setEmail(decodeURIComponent(emailFromUrl));
    } else if (propEmail) {
      setEmail(propEmail);
    }

    if (accountIdFromUrl) {
      setAccountId(decodeURIComponent(accountIdFromUrl));
    } else if (propAccountId) {
      setAccountId(propAccountId);
    }
  }, [propEmail, propAccountId]);

  const handleUnsubscribe = async () => {
    if (!email) {
      setError('No email address provided');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.post('/api/unsubscribe', {
        email: email,
        accountId: accountId,
        reason: 'User requested unsubscribe'
      });

      if (response.data.success) {
        setSuccess(true);
      } else {
        setError(response.data.message || 'Failed to unsubscribe');
      }
    } catch (error) {
      console.error('Unsubscribe error:', error);
      if (axios.isAxiosError(error)) {
        setError(error.response?.data?.error || 'Failed to unsubscribe');
      } else {
        setError('An unexpected error occurred');
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Card sx={{ textAlign: 'center', p: 4 }}>
          <CardContent>
            <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom color="success.main">
              Successfully Unsubscribed
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              You have been successfully unsubscribed from our mailing list.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Email: <strong>{email}</strong>
            </Typography>
            <Divider sx={{ my: 3 }} />
            <Typography variant="body2" color="text.secondary">
              You will no longer receive emails from us. If you change your mind, 
              you can always subscribe again through our website.
            </Typography>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Card sx={{ textAlign: 'center', p: 4 }}>
        <CardContent>
          <UnsubscribeIcon sx={{ fontSize: 80, color: 'warning.main', mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            Unsubscribe from Emails
          </Typography>
          
          {email ? (
            <>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                Are you sure you want to unsubscribe from our mailing list?
              </Typography>
              <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.100', borderRadius: 2 }}>
                <Email sx={{ mr: 1, verticalAlign: 'middle' }} />
                <Typography variant="body1" component="span">
                  {email}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                You will no longer receive promotional emails, newsletters, or campaign updates.
              </Typography>
            </>
          ) : (
            <Typography variant="body1" color="error" sx={{ mb: 3 }}>
              No email address provided. Please check your unsubscribe link or contact support.
            </Typography>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="outlined"
              onClick={() => window.history.back()}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="warning"
              onClick={handleUnsubscribe}
              disabled={loading || !email}
              startIcon={loading ? <CircularProgress size={20} /> : <UnsubscribeIcon />}
            >
              {loading ? 'Unsubscribing...' : 'Unsubscribe'}
            </Button>
          </Box>

          <Divider sx={{ my: 3 }} />
          <Typography variant="caption" color="text.secondary">
            This action cannot be undone. You can resubscribe by visiting our website.
          </Typography>
        </CardContent>
      </Card>
    </Container>
  );
};

export default Unsubscribe;
